// ABOUTME: Displays urgent baking tasks that need immediate attention.
// ABOUTME: Shows tasks with depleted stock or imminent depletion in table format.

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import TaskTableRow from './TaskTableRow';
import SortableHeaderCell from './SortableHeaderCell';
import useSortableTable from '../../hooks/useSortableTable';
import { colors } from '../../theme/colors';

function CategoryHeader({ category, totalUrgency, t }) {
  const categoryColor = category.color || colors.category.default;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        py: 1.5,
        bgcolor: `${categoryColor}33`,
        borderBottom: `1px solid ${colors.border.light}`,
      }}
    >
      <Box
        sx={{
          color: '#1f2937',
          fontSize: '0.875rem',
          fontWeight: 600,
        }}
      >
        {t('dashboard.schedule.table.category')}: {category.name}
      </Box>
      <Box
        sx={{
          fontSize: '0.8125rem',
          color: '#1f2937',
          ml: 'auto',
        }}
      >
        {t('dashboard.recommendations.priority')}: <Box component="span" sx={{ fontWeight: 700 }}>{totalUrgency.toFixed(0)}</Box>
      </Box>
    </Box>
  );
}

function UrgentTasksSection({ tasks, groupByCategory, onProductSelect, testModeEnabled = false }) {
  const { t } = useTranslation();

  // In grouped mode, only allow sorting by priority and name
  const allowedSortKeys = groupByCategory ? ['urgency_score', 'product_name', 'product_id'] : null;

  const { sortedItems, sortKey, sortDirection, handleSort, isSortable } = useSortableTable(tasks, {
    defaultSortKey: 'urgency_score',
    defaultSortDirection: 'desc',
    allowedSortKeys,
  });

  if (!tasks || tasks.length === 0) {
    return null;
  }

  // Group tasks by category (uses sortedItems for within-group sorting)
  const groupTasks = () => {
    const grouped = sortedItems.reduce((acc, task) => {
      const categoryId = task.category?.id || 'uncategorized';
      if (!acc[categoryId]) {
        acc[categoryId] = {
          category: task.category || {
            id: 'uncategorized',
            name: t('dashboard.uncategorized'),
            color: colors.category.default,
          },
          tasks: [],
          totalUrgency: 0,
        };
      }
      acc[categoryId].tasks.push(task);
      acc[categoryId].totalUrgency += task.urgency_score || 0;
      return acc;
    }, {});

    // Sort groups by total urgency (descending)
    const sortedGroups = Object.values(grouped).sort((a, b) => b.totalUrgency - a.totalUrgency);

    return sortedGroups;
  };

  const TableHeader = () => {
    const gridColumns = groupByCategory
      ? '3fr 1.5fr 1fr 1fr 0.7fr 0.7fr 0.7fr'
      : '3fr 1.5fr 1.5fr 1fr 1fr 0.7fr 0.7fr 0.7fr';

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: gridColumns,
          py: 1.5,
          px: 2,
          bgcolor: colors.schedule.urgent,
          color: 'white',
          fontSize: '0.8125rem',
          fontWeight: 600,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <SortableHeaderCell
            sortKey="product_id"
            currentSortKey={sortKey}
            sortDirection={sortDirection}
            onSort={handleSort}
            isSortable={isSortable('product_id')}
          >
            {t('dashboard.schedule.table.id')}
          </SortableHeaderCell>
          <Box>/</Box>
          <SortableHeaderCell
            sortKey="product_name"
            currentSortKey={sortKey}
            sortDirection={sortDirection}
            onSort={handleSort}
            isSortable={isSortable('product_name')}
          >
            {t('dashboard.schedule.table.product')}
          </SortableHeaderCell>
        </Box>
        {!groupByCategory && (
          <SortableHeaderCell
            sortKey="category.name"
            currentSortKey={sortKey}
            sortDirection={sortDirection}
            onSort={handleSort}
            isSortable={isSortable('category.name')}
            sx={{ textAlign: 'center' }}
          >
            {t('dashboard.schedule.table.category')}
          </SortableHeaderCell>
        )}
        <SortableHeaderCell
          sortKey="quantity"
          currentSortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          isSortable={isSortable('quantity')}
          sx={{ textAlign: 'center' }}
        >
          {t('dashboard.schedule.table.quantity')}
        </SortableHeaderCell>
        <SortableHeaderCell
          sortKey="prediction_horizon_minutes"
          currentSortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          isSortable={isSortable('prediction_horizon_minutes')}
          sx={{ textAlign: 'center' }}
        >
          {t('dashboard.schedule.table.horizon')}
        </SortableHeaderCell>
        <SortableHeaderCell
          sortKey="stock_depletes_at"
          currentSortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          isSortable={isSortable('stock_depletes_at')}
          sx={{ textAlign: 'center' }}
        >
          {t('dashboard.schedule.urgent.depletesAt')}
        </SortableHeaderCell>
        <SortableHeaderCell
          sortKey="current_stock"
          currentSortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          isSortable={isSortable('current_stock')}
          sx={{ textAlign: 'center' }}
        >
          {t('dashboard.schedule.table.stock')}
        </SortableHeaderCell>
        <SortableHeaderCell
          sortKey="prep_time"
          currentSortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          isSortable={isSortable('prep_time')}
          sx={{ textAlign: 'center' }}
        >
          {t('dashboard.schedule.table.time')}
        </SortableHeaderCell>
        <SortableHeaderCell
          sortKey="urgency_score"
          currentSortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          isSortable={isSortable('urgency_score')}
          sx={{ textAlign: 'center' }}
        >
          {t('dashboard.schedule.table.priority')}
        </SortableHeaderCell>
      </Box>
    );
  };

  return (
    <Box>
      {/* Section Title */}
      <Box
        sx={{
          fontSize: '1rem',
          fontWeight: 600,
          color: colors.schedule.urgentDark,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {t('dashboard.schedule.urgent.title')}
      </Box>

      <Box
        sx={{
          bgcolor: colors.background.white,
          borderRadius: '12px',
          border: `3px solid ${colors.schedule.urgent}`,
          overflow: 'hidden',
        }}
      >
        <TableHeader />
        {groupByCategory
          ? groupTasks().map((group) => (
              <React.Fragment key={group.category.id}>
                <CategoryHeader
                  category={group.category}
                  totalUrgency={group.totalUrgency}
                  t={t}
                />
                {group.tasks.map((task, index) => (
                  <TaskTableRow
                    key={`grouped-urgent-${group.category.id}-${task.product_id}-${index}`}
                    task={task}
                    variant="urgent"
                    showCategory={false}
                    onProductSelect={onProductSelect}
                    testModeEnabled={testModeEnabled}
                  />
                ))}
              </React.Fragment>
            ))
          : sortedItems.map((task, index) => (
              <TaskTableRow
                key={`urgent-${task.product_id}-${index}`}
                task={task}
                variant="urgent"
                onProductSelect={onProductSelect}
                testModeEnabled={testModeEnabled}
              />
            ))}
      </Box>
    </Box>
  );
}

export default UrgentTasksSection;
